import React, { useEffect, useState } from "react";
import PageTitle from "@Components/PageTitle/PageTitle";
import themeCreator from "@Themes/themeCreator";
import {
  Container,
  Typography,
  Box,
  List,
  ListItemText,
  ListItemIcon,
  Grid,
  Paper,
  CircularProgress,
  ListItemButton,
  IconButton,
  Button,
  TextField,
} from "@mui/material";

import PDFViewer from "@Components/PDFViewer/PDFViewer";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteIcon from "@mui/icons-material/Delete";
import { ListingPageContainer } from "../ListingPageTemplate/ListingPageTemplateStyles";
import useGetAll from "@Hooks/useGetAll";
import useDeleteItem from "@Hooks/useDeleteItem";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import { DeletePopup } from "@Components/Popup/DeletePopup";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import AddIcon from "@mui/icons-material/Add";
import GenericPopup from "@Components/Popup/Popup";
import usePostItem from "@Hooks/usePostItem";
import useGetManyRequests from "@Hooks/useGetManyRequests";
import SearchableDropDown from "@Components/DropDown/SearchableDropDown";
import Joi from "joi";
import {
  DropDownValidation,
  WithoutValidation,
} from "@Utils/Validations/UserValidations/UserValidationFieldsSchema";
import { submitFormWithValidation } from "@Utils/Validations/HelperFunctions/FormValidationFunctions";
import {
  MATERIALS_URL,
  SUPPLIERS_URL,
  MSDS_DOCUMENTS_URL,
  GET_ALL_MSDS_DOCUMENTS
} from "@API/APIRoutes";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import axios from "axios";
import { useDispatch } from "react-redux";
import { setSnackbar } from "@Redux/snackBarSlice";
import useSendPostRequest from "@Hooks/useSendPostRequest";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import UploadFileButton from "../../Components/Buttons/uploadFileButton";

export default function GenericMSDSFilesListing({ readOnly = false }) {
  const theme = themeCreator();
  const dispatch = useDispatch();

  const [documents, setDocuments] = useState([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showAddItemPopup, setShowAddItemPopup] = useState(false);
  const [fileName, setFileName] = useState("");
  const [MSDSfile, setMSDSFile] = useState();
  const [MSDSFileError, setMSDSFileError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const [supplierNameOptions, setSupplierNameOptions] = useState([]);
  const [materialNameOptions, setMaterialNameOptions] = useState([]);

  const [DocumentData, setDocumentData] = useState({
    supplierId: "",
    materialId: "",
    MSDSObjectKey: "",
  });
  const [errors, setErrors] = useState({});
  const [addMSDSDocumentLoading, setaddMSDSDocumentLoading] = useState(false);
  const [searchField, setSearchField] = useState("supplier"); // 'supplier' or 'material'

  const { loading: deleteLoading, deleteItem } = useDeleteItem();
  const { loading: PostItemLoading, postItem: AddMSDSDocument } = usePostItem();
  const { loading: getAllDocumentsLoading, postItem: GetAllMSDSDocument } =
    usePostItem();

  const { isLoading: minioLoading, requestMethod: minioPostData } =
    useSendPostRequest();
  const { isLoading: loading2, requestMethod: postData } =
    useSendPostRequest(true);

  const { loading: getAllLoading, getAll } = useGetManyRequests();

  const icon = <FileCopyIcon color="white" fontSize="large" />;

  const validationSchema = Joi.object({
    supplierId: DropDownValidation("Supplier", false),
    materialId: DropDownValidation("Material", false),
    MSDSObjectKey: WithoutValidation,
  });

  useEffect(() => {
    GetAllMSDSDocument(
      GET_ALL_MSDS_DOCUMENTS,
      {},
      (data) => {
        setDocuments(data);
      },
      "Documents fetched successfully",
      "Failed to fetch documents"
    );
  }, []);

  const urls = [SUPPLIERS_URL, MATERIALS_URL];
  const optionsMapping = [
    {
      index: 0,
      key: "supplierName",
      id: "supplierId",
      setter: setSupplierNameOptions,
    },
    {
      index: 1,
      key: "materialName",
      id: "materialId",
      setter: setMaterialNameOptions,
    },
  ];

  useEffect(() => {
    getAll(urls).then((res) => {
      optionsMapping.forEach(({ index, key, id, setter }) => {
        setter(
          res[index]?.map((item) => ({ name: item[key], id: String(item[id]) }))
        );
      });
    });
  }, []);

  const clearData = () => {
    setDocumentData({
      supplierId: "",
      materialId: "",
      MSDSObjectKey: "",
    });
    setErrors({});
    setMSDSFileError("");
    setFileName("");
    setMSDSFile("");
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) {
      setMSDSFileError("File Is Required.");
      return;
    }
    setMSDSFile(file);
    setFileName(file.name);
    setMSDSFileError("");
  };

  const handleAddItem = async (event) => {
    event.preventDefault();
    try {
      const newDocumentData = await submitFormWithValidation(
        DocumentData,
        false,
        null,
        setErrors,
        validationSchema
      );

      let validationFailed = false;
      if (!newDocumentData) validationFailed = true;
      if (!fileName) {
        setMSDSFileError("File Is Required.");
        validationFailed = true;
      }
      if (validationFailed) return;

      setaddMSDSDocumentLoading(true);

      const response = await AddMSDSDocument(
        MSDS_DOCUMENTS_URL,
        newDocumentData,
        () => {},
        `Document Inserted Successfully!`,
        `Failed to insert Document!`
      );
      if (response.preSignedUrl != null) {
        const { preSignedUrl, objectKey } = response;
        // Upload file to Minio
        await axios.put(preSignedUrl, MSDSfile, {
          headers: {
            "Content-Type": "application/pdf",
          },
        });
        window.location.reload();
        closeAddItemPopup();
      } else {
        dispatch(
          setSnackbar({
            open: true,
            message:
              "Document witht this material and supplier already exists.",
            error: true,
          })
        );
      }
      closeAddItemPopup();
    } catch (error) {
      console.error(error);
    } finally {
      setaddMSDSDocumentLoading(false);
    }
  };

  // if (getAllDocumentsLoading)

  const openAddItemPopup = () => setShowAddItemPopup(true);
  const closeAddItemPopup = () => setShowAddItemPopup(false);

  const confirmDelete = (documentId) => {
    deleteItem(
      documentId,
      () => {
        setDocuments((prev) =>
          prev.filter((doc) => doc.msdsDocumentId !== documentId)
        );
        setSelectedDocumentId(null);
        setShowDeletePopup(false);
      },
      MSDS_DOCUMENTS_URL,
      "Document deleted successfully",
      "Error deleting document"
    );
  };

  const popupContent = (
    <Grid container spacing={2} justifyContent="flex-end">
      <Grid item xs={6}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          Supplier Name
        </Typography>
        <SearchableDropDown
          state={DocumentData.supplierId}
          setState={(value) => {
            setDocumentData({ ...DocumentData, supplierId: value });
            setErrors({ ...errors, supplierId: "" });
          }}
          name="supplier"
          label=""
          placeholder="Select Supplier Name"
          items={supplierNameOptions}
          helperText={errors.supplierId}
        />
      </Grid>
      <Grid item xs={6}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          Material Name
        </Typography>
        <SearchableDropDown
          state={DocumentData.materialId}
          setState={(value) => {
            setDocumentData({ ...DocumentData, materialId: value });
            setErrors({ ...errors, materialId: "" });
          }}
          name="material"
          label=""
          placeholder="Select Material Name"
          items={materialNameOptions}
          helperText={errors.materialId}
        />
      </Grid>
      <Grid item xs={12}>
        <UploadFileButton
          handleFileChange={handleFileChange}
          fileName={fileName}
          fileError={MSDSFileError}
          acceptedTypes={["application/pdf"]}
          errorMessage="Only PDF files are allowed."
          buttonLabel={MSDSfile ? "Change File" : "Upload MSDS Document"}
          buttonVariant="outlined"
          buttonStartIcon={MSDSfile ? <AttachFileIcon /> : <CloudUploadIcon />}
          fullWidth={false}
          dragAndDrop={true}
          sx={{
            backgroundColor: MSDSfile
              ? "rgba(25, 118, 210, 0.08)"
              : "transparent",
          }}
        />
      </Grid>
    </Grid>
  );
  const filteredDocuments = isSearching
    ? documents?.filter((doc) => {
        const search = searchTerm.toLowerCase();
        if (searchField === "supplier") {
          return doc?.supplierName?.toLowerCase().includes(search);
        } else if (searchField === "material") {
          return doc?.materialName?.toLowerCase().includes(search);
        }
        return true;
      })
    : documents;

  if (getAllDocumentsLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={theme.pageHeight}
      >
        <LoadingComponent />
      </Box>
    );
  }

  return (
    <ListingPageContainer className="listingPageContainer">
      <Container
        sx={{ pb: 5, pr: "0px !important", pl: "0px !important" }}
        disableGutters
      >
        <Box className="LabelButtonBox">
          <PageTitle
            title="MSDS Documents"
            PageIcon={icon}
            iconColor={theme.palette.primary.main}
          />
        </Box>

        <Box sx={{ mt: 4 }}>
          {getAllLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 0,
                    height: "100%",
                    maxHeight: "70vh",
                    overflow: "auto",
                  }}
                >
                  {/* Sticky Header */}
                  <Box
                    sx={{
                      position: "sticky",
                      top: 0,
                      zIndex: 10, // High enough to overlay everything
                      backgroundColor: theme.palette.background.paper, // solid background (not transparent)
                      p: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ color: theme.palette.primary.main }}
                      >
                        MSDS Documents
                      </Typography>
                      {!readOnly && (
                        <Box
                          onClick={openAddItemPopup}
                          sx={{
                            p: 0.5,
                            borderRadius: 1.5,
                            backgroundColor: theme.palette.primary.light,
                            color: theme.palette.primary.main,
                            cursor: "pointer",
                            transition: "0.3s",
                            "&:hover": {
                              backgroundColor: theme.palette.primary.main,
                              color: theme.palette.primary.contrastText,
                            },
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <AddIcon />
                        </Box>
                      )}
                    </Box>
                    <hr
                      style={{
                        mt: 4,
                        color: theme.palette.primary.main,
                      }}
                    />

                    {/* Search Bar */}
                    <Box sx={{ display: "flex", gap: 1, pt: 1 }}>
                      <TextField
                        fullWidth
                        placeholder={`Search by ${searchField}`}
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") setIsSearching(true);
                        }}
                        InputProps={{
                          endAdornment: (
                            <Box sx={{ display: "flex", gap: 1 }}>
                              {searchTerm && isSearching && (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSearchTerm("");
                                    setIsSearching(false);
                                  }}
                                >
                                  <ClearIcon />
                                </IconButton>
                              )}
                              <IconButton
                                size="small"
                                onClick={() => setIsSearching(true)}
                              >
                                <SearchIcon />
                              </IconButton>
                            </Box>
                          ),
                        }}
                      />
                      <TextField
                        select
                        value={searchField}
                        onChange={(e) => setSearchField(e.target.value)}
                        size="small"
                        sx={{ width: 160 }}
                        SelectProps={{ native: true }}
                      >
                        <option value="supplier">Supplier</option>
                        <option value="material">Material</option>
                      </TextField>
                    </Box>
                  </Box>

                  {/* Scrollable List */}
                  <Box sx={{ p: 1.5 }}>
                    <List>
                      {filteredDocuments?.map((doc) => (
                        <ListItemButton
                          key={doc?.msdsDocumentId}
                          onClick={() => setSelectedDocument(doc)}
                          selected={selectedDocument?.msdsDocumentId === doc?.msdsDocumentId}
                          sx={{
                            mb: 1,
                            borderRadius: 1,
                            "&.Mui-selected": {
                              backgroundColor: `${theme.palette.primary.main}20`,
                              borderLeft: `4px solid ${theme.palette.primary.main}`,
                              boxShadow: `0 2px 5px ${theme.palette.primary.main}30`,
                            },
                          }}
                        >
                          <ListItemIcon>
                            <DescriptionIcon
                              color={
                                selectedDocument?.msdsDocumentId === doc?.msdsDocumentId
                                  ? "primary"
                                  : "inherit"
                              }
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={doc?.materialName}
                            secondary={`${doc?.supplierName || ""}`}
                          />
                          {!readOnly && (
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedDocumentId(doc?.msdsDocumentId);
                                setShowDeletePopup(true);
                              }}
                              size="small"
                              sx={{
                                color: theme.palette.error.main,
                                ml: "auto",
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </ListItemButton>
                      ))}
                    </List>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={8}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    height: "70vh",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: selectedDocument ? "flex-start" : "center",
                    alignItems: selectedDocument ? "stretch" : "center",
                  }}
                >
                  {!selectedDocument ? (
                    <Typography variant="body1" color="text.secondary">
                      Select a document to view its contents
                    </Typography>
                  ) : (
                    <Box sx={{ flexGrow: 1, height: "100%" }}>
                      <PDFViewer
                        fileUrl={selectedDocument?.downloadUrl}
                        style={{ height: "100%", width: "100%" }}
                      />
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </Box>
      </Container>

      {DeletePopup(
        "Document",
        showDeletePopup,
        () => setShowDeletePopup(false),
        confirmDelete,
        selectedDocumentId,
        deleteLoading
      )}

      <GenericPopup
        icon={icon}
        open={showAddItemPopup}
        title={`Add MSDS Document`}
        content={popupContent}
        buttons={[
          {
            text: "Cancel",
            onClick: closeAddItemPopup,
            color: "secondary",
            variant: "outlined",
          },
          {
            text: "Add",
            onClick: handleAddItem,
            color: "primary",
            variant: "contained",
            loading: addMSDSDocumentLoading,
          },
        ]}
      />
    </ListingPageContainer>
  );
}
